import axios from 'axios';
import { mockDocumentNumberRules } from '../data/mockData.ts';
import type { ApprovalRecord, Document, DocumentStatus, DocumentVersion } from '../types/document.ts';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const DOCUMENT_API_URL = `${BASE_API_URL}/QmsDocument`;

export interface DocumentCreateDto {
  title: string;
  description?: string;
  category: string;
  tags?: string[];
  file?: File;
  draftFileUrl?: string;
  status: string;
  approvalSteps?: any[];
  documentNumber?: string;
  currentVersion?: string;
  createdBy?: number;
  updatedBy?: number | string;
  updatedAt?: string;
  templateId?: number | string;
}

export interface DocumentResponse {
  id: string;
  documentNumber: string;
  title: string;
  description: string;
  category: string;
  status: string;
  currentVersion: string;
  tags: string[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  draftFileUrl?: string;
  templateId?: string;
}

const mapStatusToString = (status: unknown): DocumentStatus => {
  if (status === 0 || status === '0') return 'Draft';
  if (status === 1 || status === '1') return 'Review';
  if (status === 2 || status === '2') return 'Approved';
  if (status === 3 || status === '3') return 'Obsolete';

  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'draft') return 'Draft';
  if (normalized === 'review' || normalized === 'inreview' || normalized === 'underreview') return 'Review';
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'obsolete') return 'Obsolete';

  return 'Draft';
};

const mapApprovalAction = (value: unknown): ApprovalRecord['action'] => {
  if (value === 0 || value === '0') return 'Pending';
  if (value === 1 || value === '1') return 'Approved';
  if (value === 2 || value === '2') return 'Rejected';

  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'approved' || normalized === 'approve') return 'Approved';
  if (normalized === 'rejected' || normalized === 'reject') return 'Rejected';
  return 'Pending';
};

const normalizeVersions = (raw: any): DocumentVersion[] => {
  const source = raw?.versions || raw?.versionHistory || raw?.versionHistories || raw?.qmsDocumentVersions || [];
  if (!Array.isArray(source) || source.length === 0) {
    if (!raw?.draftFileUrl) return [];
    return [
      {
        version: String(raw?.currentVersion || '1.0'),
        createdAt: raw?.updatedAt || raw?.createdAt || new Date().toISOString(),
        createdBy: String(raw?.updatedBy || raw?.createdBy || ''),
        changes: '',
        status: mapStatusToString(raw?.status),
        isActive: true,
        fileUrl: raw?.draftFileUrl,
      }
    ];
  }

  return source.map((v: any, idx: number) => {
    const fileSize = Number(v?.fileSize ?? v?.size ?? 0);
    const versionValue = String(v?.version ?? v?.versionNo ?? v?.versionNumber ?? idx + 1);
    return {
      version: versionValue,
      createdAt: v?.createdAt || v?.createdOn || v?.updatedAt || raw?.updatedAt || raw?.createdAt || new Date().toISOString(),
      createdBy: String(v?.createdBy ?? v?.createdByName ?? v?.userName ?? v?.updatedBy ?? raw?.createdBy ?? ''),
      changes: v?.changes || v?.changeLog || v?.description || '',
      status: mapStatusToString(v?.status ?? raw?.status),
      isActive: Boolean(v?.isActive ?? v?.active ?? (versionValue === String(raw?.currentVersion ?? ''))),
      fileUrl: v?.fileUrl || v?.url || v?.filePath || v?.downloadUrl || undefined,
      fileSize: Number.isFinite(fileSize) && fileSize > 0 ? fileSize : undefined,
    } as DocumentVersion;
  });
};

const normalizeApprovalHistory = (raw: any): ApprovalRecord[] => {
  // Handle workflow instance structure with nested stepInstances and approvalRecords
  if (Array.isArray(raw?.stepInstances) && raw.stepInstances.length > 0) {
    const approvalRecords: ApprovalRecord[] = [];
    
    raw.stepInstances.forEach((stepInstance: any) => {
      const stepNo = stepInstance?.stepNo ?? stepInstance?.stepNumber ?? 0;
      
      if (Array.isArray(stepInstance?.approvalRecords)) {
        stepInstance.approvalRecords.forEach((record: any, recordIdx: number) => {
          approvalRecords.push({
            id: String(record?.id ?? `${raw?.id}-step${stepNo}-${recordIdx}`),
            documentId: String(record?.docId ?? raw?.docId ?? raw?.id ?? ''),
            version: String(record?.docVersion ?? raw?.docVersion ?? raw?.currentVersion ?? ''),
            approver: String(record?.approverName ?? record?.approverId ?? '-'),
            action: mapApprovalAction(record?.status ?? record?.action ?? record?.result),
            comments: String(record?.comment ?? record?.comments ?? ''),
            timestamp: String(record?.actionTime ?? record?.timestamp ?? record?.createdAt ?? new Date().toISOString()),
            level: Number(stepNo),
          });
        });
      }
    });
    
    return approvalRecords;
  }

  // Handle flat approval records structure (legacy format)
  const source =
    raw?.approvalHistory ||
    raw?.approvalHistories ||
    raw?.approvalRecords ||
    raw?.approvals ||
    raw?.workflowHistory ||
    [];

  if (!Array.isArray(source)) return [];

  return source.map((record: any, idx: number) => ({
    id: String(record?.id ?? `${raw?.id || 'doc'}-${idx}`),
    documentId: String(record?.documentId ?? raw?.id ?? ''),
    version: String(record?.version ?? record?.versionNo ?? raw?.currentVersion ?? ''),
    approver: String(record?.approver ?? record?.approverName ?? record?.userName ?? record?.createdBy ?? '-'),
    action: mapApprovalAction(record?.action ?? record?.status ?? record?.result),
    comments: String(record?.comments ?? record?.comment ?? ''),
    timestamp: String(record?.timestamp ?? record?.createdAt ?? record?.approvedAt ?? new Date().toISOString()),
    level: Number(record?.level ?? record?.stepNo ?? record?.stepNumber ?? idx + 1),
  }));
};

const normalizeDocumentResponse = (raw: any): Document => {
  const versions = normalizeVersions(raw);
  const activeVersion = versions.find(v => v.isActive) || versions[versions.length - 1];
  const tags = Array.isArray(raw?.tags)
    ? raw.tags.map((tag: any) => String(tag))
    : typeof raw?.tags === 'string'
      ? raw.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
      : [];

  return {
    id: String(raw?.id ?? ''),
    documentNumber: String(raw?.documentNumber ?? raw?.docNumber ?? ''),
    title: String(raw?.title ?? ''),
    description: String(raw?.description ?? ''),
    category: String(raw?.category ?? ''),
    status: mapStatusToString(raw?.status),
    currentVersion: String(raw?.currentVersion ?? activeVersion?.version ?? '1.0'),
    versions,
    createdAt: String(raw?.createdAt ?? raw?.createdOn ?? new Date().toISOString()),
    createdBy: String(raw?.createdBy ?? raw?.createdByName ?? ''),
    updatedAt: String(raw?.updatedAt ?? raw?.createdAt ?? new Date().toISOString()),
    updatedBy: String(raw?.updatedBy ?? raw?.updatedByName ?? raw?.createdBy ?? ''),
    tags,
    approvalHistory: normalizeApprovalHistory(raw),
    draftFileUrl: raw?.draftFileUrl || activeVersion?.fileUrl,
    templateId: raw?.templateId != null ? String(raw.templateId) : undefined,
  };
};

export const downloadDocumentFile = async (fileUrl: string, fallbackFileName = 'document'): Promise<void> => {
  const token = localStorage.getItem('token');
  const resolvedUrl = /^https?:\/\//i.test(fileUrl) ? fileUrl : new URL(fileUrl, window.location.origin).toString();

  const response = await axios.get(resolvedUrl, {
    responseType: 'blob',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  const fileNameFromHeader = contentDisposition?.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)?.[1];
  const decodedHeaderName = fileNameFromHeader ? decodeURIComponent(fileNameFromHeader.replace(/"/g, '')) : '';
  const fileNameFromUrl = decodeURIComponent((resolvedUrl.split('?')[0].split('/').pop() || '').trim());
  const fileName = decodedHeaderName || fileNameFromUrl || `${fallbackFileName}.bin`;

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Generate document number in format: QMS-{TypeAbbr}-{Year}-{Version}
 * Example: QMS-SOP-2026-001
 * @param category - Document category (e.g., 'Standard Operating Procedure', 'Work Instruction', 'SOP')
 * @param version - Document version (e.g., '1.0', '2.5'), will be converted to 3-digit format (001, 002, etc.)
 * @returns Generated document number
 */
export const generateDocumentNumber = (category: string, version?: string): string => {
  const currentYear = new Date().getFullYear();
  
  // Find the corresponding rule in mockDocumentNumberRules
  const rule = mockDocumentNumberRules.find(
    r => r.category === category || r.category.toLowerCase() === category.toLowerCase()
  );
  
  // Extract type abbreviation from prefix (e.g., 'QMS-SOP' -> 'SOP')
  let typeAbbr = 'DOC';
  if (rule && rule.prefix) {
    const parts = rule.prefix.split('-');
    if (parts.length > 1) {
      typeAbbr = parts[parts.length - 1]; // Get the last part after QMS-
    }
  } else {
    // If no rule found, generate abbreviation from category
    // Take first letter of each word (e.g., 'Standard Operating Procedure' -> 'SOP')
    const words = category.split(' ');
    typeAbbr = words.map(word => word.charAt(0).toUpperCase()).join('');
  }
  
  // Convert version to 3-digit sequence number
  let sequenceNum = '001';
  if (version) {
    // If version is like '1.0', extract the major version
    const majorVersion = parseInt(version.split('.')[0]);
    if (!isNaN(majorVersion) && majorVersion > 0) {
      sequenceNum = majorVersion.toString().padStart(3, '0');
    }
  }
  
  return `QMS-${typeAbbr}-${currentYear}-${sequenceNum}`;
};

/**
 * Create a new document
 * Calls the POST /api/QmsDocument endpoint with FormData
 */
export const createDocument = async (data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    // Create FormData to send file and other data
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('status', data.status);
    
    // Required fields
    const docNumber = data.documentNumber || generateDocumentNumber(data.category, data.currentVersion || '1.0');
    formData.append('documentNumber', docNumber);
    formData.append('currentVersion', data.currentVersion || '1.0');
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    formData.append('createdBy', data.createdBy?.toString() || currentUser || '1000');
    
    // Add templateId if provided
    if (data.templateId) {
      formData.append('templateId', data.templateId.toString());
    }
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded but draftFileUrl is provided, use it
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    const response = await axios.post<DocumentResponse>(DOCUMENT_API_URL, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to create document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create document'
    );
  }
};

/**
 * Update an existing document
 * Calls the PUT /api/QmsDocument/{id} endpoint with FormData
 */
export const updateDocument = async (id: string, data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    // Add the ID to match the URL parameter
    formData.append('id', id);
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('status', data.status);
    
    // Required fields
    if (data.documentNumber) {
      formData.append('documentNumber', data.documentNumber);
    }
    if (data.currentVersion) {
      formData.append('currentVersion', data.currentVersion);
    }
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    if (data.createdBy) {
      formData.append('createdBy', data.createdBy.toString());
    } else if (currentUser) {
      formData.append('createdBy', currentUser);
    }
    
    // Add updatedBy and updatedAt for document updates
    if (data.updatedBy) {
      formData.append('updatedBy', data.updatedBy.toString());
    } else if (currentUser) {
      formData.append('updatedBy', currentUser);
    }
    if (data.updatedAt) {
      formData.append('updatedAt', data.updatedAt);
    } else {
      // Use current timestamp if not provided
      formData.append('updatedAt', new Date().toISOString());
    }
    
    // Add templateId if provided
    if (data.templateId) {
      formData.append('templateId', data.templateId.toString());
    }
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded, preserve existing draftFileUrl
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    const response = await axios.put<DocumentResponse>(`${DOCUMENT_API_URL}/${id}`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to update document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to update document'
    );
  }
};

/**
 * Get document by ID
 */
export const getDocumentById = async (id: string): Promise<Document> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<any>(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    const payload = response.data?.data ?? response.data;
    return normalizeDocumentResponse(payload);
  } catch (error: any) {
    console.error(`Failed to get document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get document'
    );
  }
};

/**
 * Delete document by ID
 */
export const deleteDocument = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to delete document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to delete document'
    );
  }
};

/**
 * Submit document for approval
 * Calls the POST /api/QmsDocument/documents/{docId}/submit endpoint
 */
export const submitDocumentForApproval = async (
  docId: string | number,
  templateId?: number,
  approvalSteps?: any[]
): Promise<{ docId: number; id: number; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    // Add approvalStepsJson if provided
    if (approvalSteps && approvalSteps.length > 0) {
      formData.append('approvalStepsJson', JSON.stringify(approvalSteps));
    }

    const params: any = {};
    if (templateId) {
      params.templateId = templateId;
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/submit`,
      formData,
      {
        params,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to submit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to submit document for approval'
    );
  }
};

/**
 * Commit/approve a document
 * Calls the POST /api/QmsDocument/documents/{docId}/commit endpoint
 */
export const commitDocument = async (
  docId: string | number,
  action: 'Approve' | 'Reject' = 'Approve',
  comments?: string
): Promise<{ id: number; status: string; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    formData.append('action', action);
    if (comments) {
      formData.append('comments', comments);
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/commit`,
      formData,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to commit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to commit document'
    );
  }
};

const unwrapListPayload = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

export const getDocumentApprovalInfo = async (docId: string): Promise<ApprovalRecord[]> => {
  try {
    const token = localStorage.getItem('token');
    const headers = { Authorization: token ? `Bearer ${token}` : '' };
    
    // Call the endpoint: /api/QmsDocument/{id}/approval-info
    // This is the official endpoint for getting document approval information
    const response = await axios.get(`${DOCUMENT_API_URL}/${docId}/approval-info`, { headers });
    const data = response.data;
    
    // The response could be:
    // 1. A workflow instance object with stepInstances (new structure)
    // 2. A wrapped response with data property containing approval records
    // 3. A direct array of approval records
    // Pass the data directly to normalizeApprovalHistory which handles all formats
    
    return normalizeApprovalHistory(data);
  } catch (error: any) {
    console.error(`Failed to get approval info for document ${docId}:`, error);
    const status = error?.response?.status;
    if (status === 404 || status === 204) {
      return []; // Document not found or no approval info
    }
    // Log the error but don't fallback - fail explicitly if the official endpoint fails
    throw new Error(
      error?.response?.data?.message || 
      error?.response?.data?.detail || 
      `Failed to get approval info for document ${docId}`
    );
  }
};

export const getApprovalRecordsByDocumentId = async (docId: string): Promise<ApprovalRecord[]> => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: token ? `Bearer ${token}` : '' };

  const endpointAttempts: Array<() => Promise<any>> = [
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords/byDocument/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords/document/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords`, {
      headers,
      params: {
        range: '[0,999]',
        perPage: 1000,
        filter: JSON.stringify({ documentId: Number.isNaN(Number(docId)) ? docId : Number(docId) })
      }
    })
  ];

  for (const request of endpointAttempts) {
    try {
      const response = await request();
      const list = unwrapListPayload(response.data);
      return normalizeApprovalHistory({ id: docId, approvalRecords: list });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status && status !== 400 && status !== 404) {
        throw new Error(error?.response?.data?.message || error?.response?.data?.detail || 'Failed to get approval records');
      }
    }
  }

  return [];
};

export const getDocumentVersionsByDocumentId = async (docId: string): Promise<DocumentVersion[]> => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: token ? `Bearer ${token}` : '' };

  const endpointAttempts: Array<() => Promise<any>> = [
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersion/byDocument/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersions/byDocument/${docId}`, { headers }),
    () => axios.get(`${DOCUMENT_API_URL}/${docId}/versions`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersion`, {
      headers,
      params: {
        range: '[0,999]',
        perPage: 1000,
        filter: JSON.stringify({ documentId: Number.isNaN(Number(docId)) ? docId : Number(docId) })
      }
    })
  ];

  for (const request of endpointAttempts) {
    try {
      const response = await request();
      const list = unwrapListPayload(response.data);
      return normalizeVersions({ id: docId, versions: list });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status && status !== 400 && status !== 404) {
        throw new Error(error?.response?.data?.message || error?.response?.data?.detail || 'Failed to get document versions');
      }
    }
  }

  return [];
};

/**
 * Download a document file
 * Calls the GET /api/QmsDocument/documents/{docId}/download endpoint
 */
export const downloadDocument = async (docId: string | number, version?: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(
      `${DOCUMENT_API_URL}/documents/${docId}/download`,
      {
        params: version ? { version } : {},
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        responseType: 'blob',
      }
    );

    // Create a temporary URL for the blob and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `document-${docId}`;
    
    if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
      filename = contentDisposition
        .split('filename=')[1]
        .split(';')[0]
        .replace(/"/g, '')
        .replace(/'/g, '');
    }
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error(`Failed to download document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to download document'
    );
  }
};
